// Global toast system — replaces every page's local Snackbar + Alert state.
//
// Usage (anywhere under <ToastProvider>):
//   const toast = useToast();
//   toast.success("Domain updated");
//   toast.error(err.message || "Something went wrong");
//   toast.info("Scan started");  toast.warning("Heads up");
//
// Toasts stack bottom-right, are glass-styled to match the app, and auto-dismiss.
import {
  createContext,
  useContext,
  useCallback,
  useRef,
  useState,
} from "react";
import { Box, Typography, IconButton } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import CloseRoundedIcon from "@mui/icons-material/Close";
import { useThemeColors } from "../context/ThemeContext";

const ToastContext = createContext(null);

const VARIANTS = {
  success: { hex: "#10b981", Icon: CheckCircleRoundedIcon },
  error: { hex: "#ef4444", Icon: ErrorRoundedIcon },
  info: { hex: "#818cf8", Icon: InfoRoundedIcon },
  warning: { hex: "#f59e0b", Icon: WarningRoundedIcon },
};

const DEFAULT_DURATION = 3500;

function ToastItem({ toast, onClose, colors }) {
  const { hex, Icon } = VARIANTS[toast.severity] || VARIANTS.info;
  return (
    <Box
      role="alert"
      sx={{
        pointerEvents: "auto",
        minWidth: 280,
        maxWidth: 380,
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        p: 1.5,
        pl: 1.75,
        borderRadius: 2.5,
        background: colors.glassBgStrong,
        border: `1px solid ${colors.glassBorder}`,
        borderLeft: `3px solid ${hex}`,
        backdropFilter: "blur(12px)",
        boxShadow: colors.shadowLg,
        animation: "toastIn 0.28s cubic-bezier(0.16, 1, 0.3, 1)",
        "@keyframes toastIn": {
          from: { opacity: 0, transform: "translateX(16px) scale(0.97)" },
          to: { opacity: 1, transform: "translateX(0) scale(1)" },
        },
      }}
    >
      <Box
        sx={{
          width: 26,
          height: 26,
          flexShrink: 0,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: hex,
          background: `${hex}1f`,
          mt: "1px",
        }}
      >
        <Icon sx={{ fontSize: 17 }} />
      </Box>
      <Typography
        sx={{
          flex: 1,
          fontSize: 13.5,
          fontWeight: 500,
          lineHeight: 1.45,
          color: colors.text,
          pt: "2px",
          wordBreak: "break-word",
        }}
      >
        {toast.message}
      </Typography>
      <IconButton
        size="small"
        onClick={() => onClose(toast.id)}
        aria-label="Dismiss"
        sx={{
          mt: "-2px",
          mr: "-4px",
          color: colors.textFaint,
          "&:hover": { color: colors.text, background: colors.hover },
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}

export function ToastProvider({ children }) {
  const colors = useThemeColors();
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    const handle = timers.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (message, severity = "info", duration = DEFAULT_DURATION) => {
      if (!message) return;
      const id = ++idRef.current;
      setToasts((list) => [...list, { id, message, severity }]);
      const handle = setTimeout(() => dismiss(id), duration);
      timers.current.set(id, handle);
      return id;
    },
    [dismiss]
  );

  // Stable API object — success/error/info/warning + the generic show/dismiss.
  const api = useRef({
    show,
    dismiss,
    success: (m, d) => show(m, "success", d),
    error: (m, d) => show(m, "error", d),
    info: (m, d) => show(m, "info", d),
    warning: (m, d) => show(m, "warning", d),
  });
  // Keep the closures fresh without changing the object identity.
  api.current.show = show;
  api.current.dismiss = dismiss;
  api.current.success = (m, d) => show(m, "success", d);
  api.current.error = (m, d) => show(m, "error", d);
  api.current.info = (m, d) => show(m, "info", d);
  api.current.warning = (m, d) => show(m, "warning", d);

  return (
    <ToastContext.Provider value={api.current}>
      {children}
      <Box
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 2400,
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          pointerEvents: "none",
        }}
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onClose={dismiss} colors={colors} />
        ))}
      </Box>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}

export default ToastProvider;
